import { useEffect, useState } from 'react';
import type { Film } from '@/types/film';
import { typeLabel } from '@/mocks/categories';
import HeroSlide from '@/pages/home/components/HeroSlide';

interface HeroCarouselProps {
  films: Film[];
}

export default function HeroCarousel({ films }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || films.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % films.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, films.length]);

  const go = (delta: number) => {
    setActive((prev) => (prev + delta + films.length) % films.length);
  };

  return (
    <section
      aria-label="焦点影视推荐"
      className="relative w-full h-[440px] md:h-[560px] lg:h-[620px] overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {films.map((film, index) => (
        <HeroSlide
          key={film.id}
          id={film.id}
          title={film.title}
          tagline={film.tagline}
          summary={film.summary}
          backdrop={film.backdrop}
          poster={film.poster}
          typeLabelText={typeLabel(film.type)}
          genre={film.genre}
          region={film.region}
          year={film.year}
          episodes={film.episodes}
          rating={film.rating}
          active={index === active}
        />
      ))}

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一个"
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-50/10 backdrop-blur border border-background-50/20 text-background-50 items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一个"
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-50/10 backdrop-blur border border-background-50/20 text-background-50 items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div className="absolute bottom-5 md:bottom-7 left-0 right-0 z-20 px-4 md:px-8 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          {films.map((film, index) => (
            <button
              key={film.id}
              type="button"
              onClick={() => setActive(index)}
              aria-label={`切换到 ${film.title}`}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                index === active ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/40 hover:bg-background-50/70'
              }`}
            />
          ))}
        </div>
        <span className="hidden sm:flex items-center gap-2 text-[11px] text-background-200/70">
          <i className="ri-settings-3-line" />
          自动轮播 · 悬停暂停
          <span className="text-background-50 font-semibold">
            {String(active + 1).padStart(2, '0')} / {String(films.length).padStart(2, '0')}
          </span>
        </span>
      </div>
    </section>
  );
}